import React from 'react'
import TeamIcon from '../users/TeamIcon'
import { UserStream } from 'components/filters/FilterIcon'
import ChannelStats from 'components/shared/ChannelStats'
import useGetUser from 'refactor/hooks/api/useGetUser'
import StreamName from '../filters/StreamName'
import useStore from '../../state/knovStore'
import { ITeam } from 'state/knovStore'
import cn from 'classnames'
import styles from './stream-embed.module.scss'
import { v4 as uuid } from 'uuid'
import useOpenRight from 'refactor/hooks/useOpenRight'
import useTeamLookup from 'components/teams/useTeamLookup'

import usePanelContext from 'refactor/hooks/usePanelContext'
import { mediaBucketsEnabled } from 'lib/mediaBuckets'
import { streamHubPath } from 'lib/seoPaths'
import { Waves } from 'lucide-react'
import { useStreamCardStats } from 'components/shared/useStreamCardStats'
import { isMobile } from 'lib/device'
import {
    getSwiperRef,
    slideTo,
    SWIPE_SPEED_FAST,
    SWIPE_SPEED_SLOW,
} from 'state/imperativeApis/swiperApi'
import type { Filter } from 'components/filters/useStreamFilters'

interface StreamEmbedProps {
    streamId?: string
    stream?: ITeam
    // Stream can be a team stream or other types
    filter?: Filter
    className?: string
    onMouseEnter?: (ev: React.MouseEvent) => void
    onMouseLeave?: (ev: React.MouseEvent) => void
    onContentLayoutSettled?: () => void
    // Metadata props
    lastActivity?: string
    questCount?: number
}

const StreamEmbed: React.FC<StreamEmbedProps> = ({
    streamId,
    stream,
    filter: providedFilter,
    className,
    onMouseEnter,
    onMouseLeave,
    onContentLayoutSettled,
    lastActivity,
    questCount,
}) => {
    const { panelToRight, panel } = usePanelContext()
    const { openRight } = useOpenRight(panel?.panelId)

    const { team } = useTeamLookup(streamId || stream?.id, stream)

    // State for banner
    const [hasBanner, setHasBanner] = React.useState(false)
    const [bannerChecked, setBannerChecked] = React.useState(false)
    const [bannerKey] = React.useState(Date.now())

    // The channel's host, shown under the name like the channel header does.
    // The name arrives with the card stats; the avatar once the user loads.
    const { hostName } = useStreamCardStats(team)
    const hostId = team?.admin_id ? String(team.admin_id) : undefined
    const { user: hostUser } = useGetUser(hostId, { refetchOnMount: false })

    // Determine the filter for StreamName component
    let filter = providedFilter
    if (!filter && (streamId || team?.id)) {
        filter = { team_ids: [streamId || team.id] }
    }

    if (!filter) {
        return null
    }

    const teamId = team?.id || streamId
    const filterSettledKey = JSON.stringify(filter)

    React.useEffect(() => {
        if (!onContentLayoutSettled) return

        const notify = () => onContentLayoutSettled()
        const frameId =
            typeof window.requestAnimationFrame === 'function'
                ? window.requestAnimationFrame(notify)
                : window.setTimeout(notify, 0)

        return () => {
            if (typeof window.cancelAnimationFrame === 'function') {
                window.cancelAnimationFrame(frameId)
            } else {
                window.clearTimeout(frameId)
            }
        }
    }, [bannerChecked, filterSettledKey, hasBanner, onContentLayoutSettled, teamId])

    const onClick = async (ev: React.MouseEvent) => {
        if (ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) {
            // Keep the parent post's click handler from swallowing the native
            // new-tab gesture without cancelling the anchor's default action.
            ev.stopPropagation()
            return
        }
        ev.preventDefault()
        ev.stopPropagation()

        const rightFilter = panelToRight()?.filter
        const rightTeamIds = rightFilter?.team_ids || []
        const numPanels = useStore.getState().numPanels

        if (teamId && rightTeamIds.includes(teamId) && rightTeamIds.length === 1) {
            // If the stream is already in the panel to the right
            if (isMobile || numPanels === 1) {
                // In one-panel mode or mobile, slide to the existing panel
                const swiper = getSwiperRef()?.current
                if (swiper) {
                    const panelIndex = useStore
                        .getState()
                        .panels.state.findIndex(p => p.panelId === panelToRight()?.panelId)
                    if (panelIndex >= 0) {
                        if (numPanels === 1 && !isMobile) {
                            slideTo(panelIndex, SWIPE_SPEED_SLOW)
                        } else {
                            swiper.slideTo(panelIndex, SWIPE_SPEED_FAST)
                        }
                    }
                }
            } else {
                panelToRight()?.shake()
            }
        } else {
            const rightPanelId = uuid()

            // Check if the team ID matches Twetch or Hodlocker and set appropriate filter
            let filterToUse = filter
            if (teamId && teamId === gon.TWETCH_TEAM_ID) {
                filterToUse = { twetch: true }
            } else if (teamId && teamId === gon.HODLOCKER_TEAM_ID) {
                filterToUse = { hodlocker: true }
            }

            openRight(filterToUse, { panelId: rightPanelId })
        }
    }

    const handleMouseEnter = (ev: React.MouseEvent) => {
        // Here we do the opposite entry function bc entering this component is equivalent to leaving the parent comp.
        if (onMouseLeave) onMouseLeave(ev)
    }

    const handleMouseLeave = (ev: React.MouseEvent) => {
        // Here we do the opposite entry function bc leaving this component is equivalent to entering the parent comp.
        if (onMouseEnter) onMouseEnter(ev)
    }

    return (
        <a
            className={cn(styles.streamEmbed, className)}
            href={streamHubPath(teamId)}
            onClick={onClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {team && (
                <div
                    className={cn(
                        hasBanner ? styles.bannerContainer : styles.bannerPlaceholder,
                        // Static geometry pins the banner region to a constant height so the
                        // embed's total height stays width-independent inside its fixed window
                        // (STREAM_EMBED_WINDOW_PX in lib/answerPlaceholderHeight) and the
                        // stream name below never gets clipped by the aspect-ratio banner.
                        mediaBucketsEnabled() && styles.bannerPinned,
                    )}
                >
                    {hasBanner ? (
                        <img
                            key={`banner-${bannerKey}`}
                            src={`${window.knovApiUrl}/api/v1/teams/${team.id}/banner?size=small&t=${bannerKey}`}
                            alt=""
                            className={styles.banner}
                        />
                    ) : (
                        <Waves className={styles.wavesIcon} size={40} />
                    )}
                </div>
            )}
            {team && (
                <img
                    key={`banner-check-${bannerKey}`}
                    src={`${window.knovApiUrl}/api/v1/teams/${team.id}/banner?size=small&t=${bannerKey}`}
                    onError={() => {
                        setHasBanner(false)
                        setBannerChecked(true)
                    }}
                    onLoad={() => {
                        setHasBanner(true)
                        setBannerChecked(true)
                    }}
                    style={{ display: 'none' }}
                    alt=""
                />
            )}
            <div className={styles.streamBody}>
                {team ? (
                    <div className={styles.logoWrap}>
                        <TeamIcon
                            team={team}
                            type="embed"
                            contextStyles={styles.filterIcon}
                            tip={`${team.name} Channel`}
                            borderStyles={styles.teamIconBorder}
                            initialStyles={styles.teamIconInitial}
                        />
                    </div>
                ) : (
                    <div className={styles.genericStreamIcon}>
                        <Waves size={24} />
                    </div>
                )}
                <div className={styles.streamName}>
                    {team ? (
                        <div className={styles.streamNameText}>{team.name}</div>
                    ) : (
                        <StreamName filter={filter} contextStyles={styles.streamNameText} />
                    )}
                </div>
                {hostName && (
                    <div className={styles.hostedBy}>
                        <span>Hosted by</span>
                        {hostUser?.id && (
                            <UserStream
                                user={hostUser}
                                size="small"
                                disable
                                contextStyles={styles.hostAvatar}
                            />
                        )}
                        <span className={styles.hostName}>@{hostName}</span>
                    </div>
                )}
                {team?.description && <div className={styles.description}>{team.description}</div>}
            </div>
            <div className={styles.streamMeta}>
                <ChannelStats team={team} />
                {questCount !== undefined && (
                    <span className={styles.metaItem}>
                        {questCount} thread{questCount !== 1 ? 's' : ''}
                    </span>
                )}
                {lastActivity && (
                    <span className={styles.metaItem}>
                        <i className="fa fa-clock-o" />
                        {lastActivity}
                    </span>
                )}
            </div>
        </a>
    )
}

export default StreamEmbed
